import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWallets,
  fetchWalletById,
  createWallet,
  updateWalletStatus,
  getSupportedNetworks,
  getWalletCoins,
  // Removed unused import: getWalletNetworks
  getWalletCoinNetworks,
  getWalletBalance,
  fetchHotWalletsByNetworkAndCoin
} from '../api/wallet';

/**
 * Hook for fetching wallets with pagination and filtering
 * @param filters Filter criteria
 * @param limit Maximum number of wallets to return
 * @param skip Number of wallets to skip (for pagination)
 * @param searchTerm Search term for wallet address or user ID
 * @param refreshInterval Interval in milliseconds to refresh data (default: 60000)
 * @returns Query result with wallets
 */
export const useWallets = (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  searchTerm: string = '',
  refreshInterval = 60000
) => {
  return useQuery({
    queryKey: ['wallets', filters, limit, skip, searchTerm],
    queryFn: () => fetchWallets(filters, limit, skip, searchTerm),
    refetchInterval: refreshInterval
  });
};

/**
 * Hook for fetching a wallet by ID
 * @param id Wallet ID
 * @returns Query result with wallet details
 */
export const useWalletById = (id: string) => {
  return useQuery({
    queryKey: ['wallet', id],
    queryFn: () => fetchWalletById(id),
    enabled: !!id
  });
};

/**
 * Hook for creating a new wallet
 * @returns Mutation for creating a wallet
 */
export const useCreateWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      network: string;
      is_evm: number;
      coin: string;
      userid: number;
      is_new?: number; // Made optional to allow backend to determine whether to return existing wallet
      type?: string;
      status?: string;
      memo?: string | null;
      coin_network_id?: string;
    }) => createWallet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
    }
  });
};

/**
 * Hook for updating wallet status
 * @returns Mutation for updating wallet status
 */
export const useUpdateWalletStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      updateWalletStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', variables.id] });
    }
  });
};

/**
 * Hook for fetching supported networks
 * @returns Query result with supported networks
 */
export const useSupportedNetworks = () => {
  return useQuery({
    queryKey: ['networks'],
    queryFn: getSupportedNetworks
  });
};

/**
 * Hook for fetching supported coins
 * @returns Query result with supported coins
 */
export const useSupportedCoins = () => {
  return useQuery({
    queryKey: ['coins'],
    queryFn: getWalletCoins
  });
};

/**
 * Hook for fetching coin-network relationships
 * @returns Query result with coin-network relationships
 */
export const useCoinNetworks = () => {
  return useQuery({
    queryKey: ['coinNetworks'],
    queryFn: getWalletCoinNetworks
  });
};

/**
 * Hook for fetching wallet balance
 * @param address Wallet address
 * @param network Network name
 * @param is_evm Whether the network is EVM-compatible
 * @param coin_symbol Optional coin symbol
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with wallet balance
 */
export const useWalletBalance = (
  address: string,
  network: string,
  is_evm: number,
  coin_symbol?: string,
  refreshInterval = 30000
) => {
  return useQuery({
    queryKey: ['walletBalance', address, network, is_evm, coin_symbol],
    queryFn: () => getWalletBalance(address, network, is_evm, coin_symbol),
    refetchInterval: refreshInterval,
    enabled: !!address && !!network
  });
};

/**
 * Hook for fetching hot wallets by network and coin
 * @param networkId Network ID or full_name
 * @param coinId Coin ID or symbol
 * @param enabled Whether the query should be enabled
 * @returns Query result with hot wallets
 */
export const useHotWalletsByNetworkAndCoin = (
  networkId: string,
  coinId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['hotWallets', networkId, coinId],
    queryFn: () => fetchHotWalletsByNetworkAndCoin(networkId, coinId),
    enabled: enabled && !!networkId && !!coinId
  });
};
